import { useNavigate } from "react-router-dom";
import { CloudOff, Download, RefreshCw, ShieldAlert, ShieldOff, X } from "lucide-react";
import { useSystemHealth } from "@/hooks/useSystemHealth";
import { t } from "@/lib/i18n";

type BannerKind = "destructive" | "warning" | "info";

export function BannerSlot() {
  const { license, update, connection, dismissedUpdate, dismissUpdate, refresh } = useSystemHealth();
  const navigate = useNavigate();

  // ── Priorität: Verbindung > Lizenz-Hard > Lizenz-Warn > Update ───────
  if (connection === "lost") {
    return (
      <Banner
        kind="destructive"
        Icon={CloudOff}
        title={t("Verbindung zum Server verloren")}
        body={t("Letzter Poll ist fehlgeschlagen. Logs-Stream ist möglicherweise auch betroffen.")}
        action={
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-current/10 px-2.5 py-1 text-xs hover:bg-current/20"
          >
            <RefreshCw className="h-3 w-3" /> {t("Erneut versuchen")}
          </button>
        }
      />
    );
  }

  if (license && (license.status === "unlicensed" || license.status === "read_only")) {
    return (
      <Banner
        kind="destructive"
        Icon={ShieldOff}
        title={
          license.status === "unlicensed"
            ? t("Setup-Modus — keine Lizenz installiert")
            : t("Read-Only-Modus — Ingest gesperrt")
        }
        body={
          license.status === "unlicensed"
            ? t("Der Server akzeptiert keine neuen Log-Einträge, bis eine Lizenz eingespielt ist.")
            : t("Lizenz abgelaufen, Grace-Period überschritten. Neue Logs werden nicht mehr angenommen.")
        }
        action={
          <button
            type="button"
            onClick={() => navigate("/einstellungen/lizenz")}
            className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-current/10 px-2.5 py-1 text-xs hover:bg-current/20"
          >
            Lizenz öffnen
          </button>
        }
      />
    );
  }

  if (license && license.status === "grace") {
    const days = license.days_remaining ?? 0;
    return (
      <Banner
        kind="warning"
        Icon={ShieldAlert}
        title={
          days === 1
            ? t("Lizenz-Grace-Period: noch 1 Tag")
            : t("Lizenz-Grace-Period: noch {n} Tage", { n: days })
        }
        body={t("Danach wird der Ingest gesperrt. Lizenz jetzt verlängern.")}
        action={
          <button
            type="button"
            onClick={() => navigate("/einstellungen/lizenz")}
            className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-current/10 px-2.5 py-1 text-xs hover:bg-current/20"
          >
            Lizenz öffnen
          </button>
        }
      />
    );
  }

  if (update && update.update_available && update.latest && update.latest !== dismissedUpdate) {
    const goToUpdates = (autoTrigger: boolean) =>
      navigate("/einstellungen/updates", { state: autoTrigger ? { autoTrigger: true } : undefined });
    return (
      <Banner
        kind="info"
        Icon={Download}
        title={t("Update v{v} verfügbar", { v: update.latest })}
        body={t("Installiert ist v{v}.", { v: update.current })}
        action={
          <div className="flex items-center gap-1">
            {update.trigger_enabled ? (
              <button
                type="button"
                onClick={() => goToUpdates(true)}
                className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-current/10 px-2.5 py-1 text-xs hover:bg-current/20"
              >
                {t("Installieren")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => goToUpdates(false)}
                className="inline-flex items-center gap-1 rounded-md border border-current/40 bg-current/10 px-2.5 py-1 text-xs hover:bg-current/20"
              >
                {t("Details")}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismissUpdate(update.latest!)}
              aria-label={t("Hinweis ausblenden")}
              className="rounded-md p-1 text-current/70 hover:text-current"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        }
      />
    );
  }

  return null;
}

function Banner({
  kind,
  Icon,
  title,
  body,
  action,
}: {
  kind: BannerKind;
  Icon: typeof CloudOff;
  title: string;
  body?: string;
  action?: React.ReactNode;
}) {
  const cls =
    kind === "destructive"
      ? "bg-destructive/10 text-destructive border-destructive/30"
      : kind === "warning"
        ? "bg-warning/10 text-warning border-warning/30"
        : "bg-accent text-accent-foreground border-border";
  return (
    // Mobil: Titel/Text untereinander, Aktion darf in eine eigene Zeile umbrechen.
    <div
      role="status"
      className={`flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2 text-sm md:px-4 ${cls}`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
        <span className="font-medium">{title}</span>
        {body ? <span className="text-[12px] opacity-80 sm:truncate">{body}</span> : null}
      </div>
      {action ? <div className="flex shrink-0 items-center gap-1">{action}</div> : null}
    </div>
  );
}
